'use client';
import { type DocumentNode } from '@apollo/client';
import {
  Breakpoints,
  ButtonSize,
  Carousel,
  Faq,
  type FaqItem,
  FontWeight,
  humanizeTier,
  IconCheck,
  IconRefund,
  IconVideo,
  IconVideoAsync,
  Tag,
  TagColor,
  TagSize,
  Tooltip,
  useMediaQuery,
} from '@leland-dev/leland-ui-library';
import { useRouter } from 'next/compat/router';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import {
  type FC,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  CoachingDeliveryFormat,
  type OfferingPackagePrice,
  PackageStatus,
  RefundPolicy,
  type Scalars,
  SkuTier,
} from '../../__generated-gql-types__/globalTypes';
import { useAuth } from '../../context/AuthContext';
import { LexSlug, useLex } from '../../context/LexContext';
import { setLocalCheckoutOrderInputs } from '../../utils/checkout';
import {
  MEET_WITH_TEAM_MEMBER_CAL_URL,
  SUBSCRIBER_DUMMY_COACH_URN,
  TYPEFORM_URLS,
} from '../../utils/constants';
import { logException } from '../../utils/exception';
import { useComponentBoundingBox } from '../../utils/hooks/useComponentBoundingBox';
import { useCountLines } from '../../utils/hooks/useCountLines';
import { DEFAULT_HOURLY_ORDER_MINUTES } from '../../utils/order';
import {
  REFUND_POLICY_DETAILS,
  REFUND_POLICY_HUMAN_READABLE,
} from '../../utils/package';
import { DEFAULT_REVIEW_FORM_VALUES } from '../../utils/review';
import {
  convertUrlSearchParamsToObject,
  getHashUrlString,
  getSignupRedirectString,
  getUrlString,
} from '../../utils/url';
import { useLoginModal } from '../LoginModal';
import SvgIcon, { Svg } from '../SvgIcon';
import { VerticalCoachCard } from '../category/VerticalCoachCard';
import CoachOfferings from '../coach/CoachOfferings';
import IntroVideo from '../coach/IntroVideo';
import ReviewSlider from '../coach/ReviewSlider';
import { useAllCoachReviewsModal } from '../coach/modals/AllCoachReviewsModal';
import { useBookHourlyCoachingModal } from '../coach/modals/BookHourlyCoachingModal';
import { useMessageModal } from '../coach/modals/MessageModal';
import InfoWithIcon from '../common/InfoWithIcon';
import LelandExperienceGuarantee from '../common/LelandExperienceGuarantee';
import { useLeaveAReviewModal } from '../review/modals/LeaveAReviewModal';
import { useOutcomeModal } from '../review/modals/OutcomeModal';

import { useAllPackageReviewsModal } from './AllPackageReviewsModal';
import OfferingPackageBanner from './OfferingPackageBanner';
import OfferingPackageBottomCTA from './OfferingPackageBottomCTA';
import OfferingPackageCard from './OfferingPackageCard';
import OfferingPackageCoachAbout from './OfferingPackageCoachAbout';
import { OfferingPackageImage } from './OfferingPackageImage';
import OfferingPackageInfo from './OfferingPackageInfo';
import OfferingPackagePricingInfo from './OfferingPackagePricingInfo';
import PackageReviews from './PackageReviews';
import ScheduleACallSection from './ScheduleACallSection';
import { useSelectAPackageSize } from './SelectAPackageSizeModal';
import { useSelectATierModal } from './SelectATierModal';
import {
  type OfferingPackagePageCoachPackageFragment,
  type OfferingPackagePageOfferingPackageFragment,
  type OfferingPackagePageOfferingPackagePriceFragment,
  type OfferingPackagePageOpportunityCoachFragment,
  useOfferingPackagePageCreateOrderMutation,
  useOfferingPackagePageGetCoachQuery,
  useOfferingPackagePageGetHourlyOrderQuery,
  useOfferingPackagePageGetRelationshipQuery,
  useOfferingPackagePageSearchCoachesQuery,
  useOfferingPackagePageSearchPackagesQuery,
} from './__generated-gql-types__/OfferingPackagePage.generated';
const ASYNC_COACHING_TOOLTIP = (
  <Tooltip content="Asynchronous coaching is done offline, rather than in live sessions. It can be great for any work that doesn't require live collaboration.">
    <span className="underline underline-offset-2">asynchronous coaching</span>
  </Tooltip>
);

const DELIVERY_FORMAT_TEXT: Record<
  CoachingDeliveryFormat,
  string | ReactElement
> = {
  [CoachingDeliveryFormat.LIVE]: 'live sessions',
  [CoachingDeliveryFormat.ASYNC]: ASYNC_COACHING_TOOLTIP,
  [CoachingDeliveryFormat.HYBRID]: (
    <>live sessions and {ASYNC_COACHING_TOOLTIP}</>
  ),
};

const SECTION_SPACING_CLASSNAME = 'mx-auto mb-12 max-w-page px-4 lg:px-6';

interface PublishedOfferingPackagePageProps {
  coach?: OfferingPackagePageOpportunityCoachFragment;
  coachView?: boolean;
  offeringPackage: OfferingPackagePageOfferingPackageFragment;
  coachPackages: Possible<OfferingPackagePageCoachPackageFragment[]>;
  refetchQueries?: DocumentNode[];
}

const CoachMatchingSupportBanner = () => (
  <div className="mt-4 flex w-full gap-2 bg-leland-primary-extra-light p-3">
    <IconCheck className="mt-1 size-4 shrink-0" />
    <p className="text-leland-dark-green">
      <span className="font-medium">Includes coach matching support.</span> If
      you&apos;re not sure, the Leland team will help match you with the perfect
      coach based on your background, goals, and budget. Want to chat with a
      team member before purchasing?{' '}
      <Link
        className="underline"
        target="_blank"
        href={MEET_WITH_TEAM_MEMBER_CAL_URL}
        id="coach_matching_support_banner--schedule_call"
      >
        Schedule a call
      </Link>
    </p>
  </div>
);

const OfferingPackagePage: FC<PublishedOfferingPackagePageProps> = ({
  coachView = false,
  offeringPackage,
  coach,
  coachPackages,
  refetchQueries,
}) => {
  const { currentUser } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const coachTier = searchParams?.get('coachTier') as SkuTier | undefined; // TODO: Remove this once we ramp coach set price
  const isSm = useMediaQuery(Breakpoints.SM);
  const isMd = useMediaQuery(Breakpoints.MD);
  const isLg = useMediaQuery(Breakpoints.LG);
  const { isLexEnabled } = useLex();
  const isRampCoachSetPriceEnabled = isLexEnabled(LexSlug.RAMP_COACH_SET_PRICE);
  const styleAsCoachPackage = isRampCoachSetPriceEnabled
    ? coach
    : !offeringPackage.isUniversalPackage;

  const opportunityCoachId = searchParams.get(
    'opportunityCoachId',
  ) as Optional<string>;

  const [hasReviewed, setHasReviewed] = useState(false);

  const [tierToUse, setTierToUse] = useState<SkuTier>(
    coachTier ??
      coach?.tier ??
      offeringPackage.prices?.at(0)?.tier ??
      SkuTier.ELITE_PLUS,
  );
  const pricesForSelectedCoachTier = useMemo(
    () =>
      offeringPackage.isUniversalPackage
        ? offeringPackage.prices?.filter((price) => price.tier === tierToUse)
        : null,
    [tierToUse, offeringPackage.isUniversalPackage, offeringPackage.prices],
  );

  useMemo(() => {
    if (coachTier) {
      setTierToUse(coachTier);
    }
  }, [coachTier]);

  const { setOpen: setLoginModalOpen, Modal: LoginModal } = useLoginModal();
  const {
    setOpen: setSelectAPackageSizeModalOpen,
    Modal: SelectAPackageSizeModal,
  } = useSelectAPackageSize();
  const { setOpen: setSelectATierModalOpen, Modal: SelectATierModal } =
    useSelectATierModal();
  const { setOpen: setIsReviewModalOpen, Modal: LeaveAReviewModal } =
    useLeaveAReviewModal();
  const { setOpen: setIsAllReviewsModalOpen, Modal: AllCoachReviewsModal } =
    useAllCoachReviewsModal();
  const {
    setOpen: setIsAllPackageReviewsModalOpen,
    Modal: AllPackageReviewsModal,
  } = useAllPackageReviewsModal();
  const { setOpen: setIsOutcomeModalOpen, Modal: OutcomeModal } =
    useOutcomeModal();
  const { setOpen: setMessageModalOpen, Modal: MessageModal } =
    useMessageModal();

  const onReviewSuccess = () => {
    setHasReviewed(true);
    setIsReviewModalOpen(false);
    setIsOutcomeModalOpen(true);
  };
  const {
    open: isBookHourlyCoachingModalOpen,
    setOpen: setIsBookHourlyCoachingModalOpen,
    Modal: BookHourlyCoachingModal,
  } = useBookHourlyCoachingModal();

  const hourlyOrderUrn = searchParams?.get('order') as
    | Scalars['OrderUrn']['output']
    | undefined;

  const { data: hourlyOrderData, loading: hourlyOrderLoading } =
    useOfferingPackagePageGetHourlyOrderQuery({
      variables: {
        id: hourlyOrderUrn ?? '',
      },
      skip: !hourlyOrderUrn,
    });

  const { data: opportunityCoachData } = useOfferingPackagePageGetCoachQuery({
    variables: { id: opportunityCoachId ?? '' },
    skip: !opportunityCoachId || !!coach || isRampCoachSetPriceEnabled,
  });

  const coachToUse = coach ?? opportunityCoachData?.coach;
  const coachIdToUse =
    opportunityCoachId ?? coach?.id ?? opportunityCoachData?.coach?.id;

  const { data: coachesData } = useOfferingPackagePageSearchCoachesQuery({
    variables: {
      categoryUrn: offeringPackage.category?.id,
      skuTiers: tierToUse ? [tierToUse] : [],
      count: 12,
    },
    skip: !offeringPackage.isUniversalPackage || coachView,
  });

  const isLelandPackage =
    offeringPackage.coach.id === SUBSCRIBER_DUMMY_COACH_URN;

  const [bannerRef, bannerSize] = useComponentBoundingBox<HTMLDivElement>();

  const { data: personalizedCoachData, refetch: refetchPersonalizedCoach } =
    useOfferingPackagePageGetRelationshipQuery({
      variables: { coachUrn: coach?.id ?? '' },
    });

  const [createOrder, { loading: createOrderLoading }] =
    useOfferingPackagePageCreateOrderMutation();

  const [packageDescriptionLines, packageDescriptionRef] = useCountLines();

  const [expandedPackageDescription, setExpandedPackageDescription] =
    useState(false);
  const [chosenTier, setChosenTier] =
    useState<Nullable<OfferingPackagePageOfferingPackagePriceFragment>>(null);

  const isLoggedIn = currentUser?.applicant;
  const coachingRelationshipExists =
    !!personalizedCoachData?.coach?.coachingRelationship;

  const mostRecentOrder = coach?.mostRecentOrder;
  const mostRecentHourlyOrder = mostRecentOrder?.hourlyOrder ?? null;

  const isUserBlocked = !!coach?.coachingRelationship?.blockedByCoach;

  const livePackages = useMemo(
    () =>
      coachPackages?.filter(
        (p) =>
          p.status === PackageStatus.LIVE &&
          p.id !== offeringPackage.id &&
          p.category?.id === offeringPackage.category?.id,
      ) ?? [],
    [offeringPackage.category?.id, coachPackages, offeringPackage.id],
  );

  const reorganizedCoaches = useMemo(() => {
    if (!coachesData?.searchCoaches.results) {
      return coachToUse ? [coachToUse] : [];
    }

    const coaches = [...coachesData.searchCoaches.results];

    if (coachToUse && !coaches.some((coach) => coach.id === coachToUse?.id)) {
      coaches.unshift(coachToUse);
    }

    coaches.sort((a, b) =>
      b.id === coachIdToUse ? 1 : a.id === coachIdToUse ? -1 : 0,
    );

    return coaches;
  }, [coachesData, coachIdToUse, coachToUse]);

  const services = coach?.categoryInformationList?.find(
    (c) => c.category.id === offeringPackage.category?.id,
  )?.coachingFocusList;

  useEffect(() => {
    if (hourlyOrderUrn) {
      setIsBookHourlyCoachingModalOpen(true);
    }
  }, [hourlyOrderUrn, setIsBookHourlyCoachingModalOpen]);

  const handleMessageCoach = async () => {
    if (!isLoggedIn) {
      setLoginModalOpen(true);
    } else if (coachingRelationshipExists) {
      await router?.push(getUrlString('/inbox', { coach: coach?.id ?? '' }));
    } else {
      setMessageModalOpen(true);
    }
  };

  const canWorkWithCoach = opportunityCoachData?.coach?.tier === tierToUse;

  const createPackageOrder = useCallback(
    async (tier?: OfferingPackagePageOfferingPackagePriceFragment) => {
      if (offeringPackage) {
        if (currentUser) {
          if (!currentUser.applicant) {
            return router?.push(
              getSignupRedirectString(getUrlString(pathname, searchParams)),
            );
          }
          const resp = await createOrder({
            variables: {
              coachUrn: coachIdToUse || offeringPackage.coach.id,
              packageOrder: {
                packageUrn: offeringPackage.id,
                version: offeringPackage.version,
                tier: tier?.tier,
                sku: tier?.sku,
              },
              numberOfCharges: 1,
            },
          });

          const orderId = resp.data?.createOrder?.id;

          if (orderId == null || resp.errors) {
            const exceptionMessage = `Failed to create package order orderId=${orderId}`;
            logException(exceptionMessage);
            throw new Error(exceptionMessage);
          }

          await router?.push(
            getUrlString('/checkout', {
              order: orderId,
              opportunityCoachId:
                coachIdToUse && canWorkWithCoach ? coachIdToUse : undefined,
            }),
          );
        } else {
          setLocalCheckoutOrderInputs({
            coachUrn: offeringPackage.coach.id,
            categoryId: offeringPackage.category?.id,
            packageOrder: {
              version: offeringPackage.version,
              packageUrn: offeringPackage.id,
              tier: tier?.tier,
              sku: tier?.sku,
            },
          });

          await router?.push('/checkout');
        }
      }
    },
    [
      canWorkWithCoach,
      createOrder,
      currentUser,
      offeringPackage,
      coachIdToUse,
      router,
      pathname,
      searchParams,
    ],
  );

  const handlePurchaseOrSelectTier = async () => {
    if (offeringPackage?.prices && offeringPackage.prices.length > 0) {
      if (offeringPackage.prices.length === 1 && !coachView) {
        await createPackageOrder(offeringPackage.prices.at(0));
      } else if (pricesForSelectedCoachTier?.length === 1 && !coachView) {
        await createPackageOrder(pricesForSelectedCoachTier[0]);
      } else if (offeringPackage.isUniversalPackage || coachView) {
        setSelectAPackageSizeModalOpen(true);
      } else {
        setSelectATierModalOpen(true);
      }
    }
  };

  const handleChoosePackage = async (tier: OfferingPackagePrice) => {
    setChosenTier(tier ?? null);
    await createPackageOrder(tier);
  };

  const { data: offeringPackagesData } =
    useOfferingPackagePageSearchPackagesQuery({
      variables: {
        categoryUrn: offeringPackage.category?.id ?? '',
        start: 0,
        count: 4,
        idsToExclude: offeringPackage.id,
      },
      skip: !offeringPackage.category?.id || !styleAsCoachPackage,
    });

  const relatedPackages = offeringPackagesData?.searchPackages.results ?? [];
  const handleBuyCoaching = () => setIsBookHourlyCoachingModalOpen(true);

  const FAQ_QUESTIONS: FaqItem[] = useMemo(
    () => [
      {
        question: `What’s the difference between the ${humanizeTier(SkuTier.BASE)}, ${humanizeTier(SkuTier.PRO)}, and ${humanizeTier(SkuTier.ELITE)} tier coaches?`,
        answer: `Leland offers three tiers of coaches in order to match you with the best coach for your budget and goals. ${humanizeTier(SkuTier.BASE)} coaches tend to be earlier in their careers or newer to Leland’s platform, but still vetted through our thorough vetting process and incredibly skilled. ${humanizeTier(SkuTier.PRO)} coaches tend to have 4-8 years of experience and a proven track record on Leland. ${humanizeTier(SkuTier.ELITE)} coaches are the best of the best, and tend to be professional coaches with hiring manager, admissions committee, or other top tier backgrounds.`,
      },
      {
        question: 'How does Leland vet the coaches on the platform?',
        answer:
          'Leland has an incredibly high bar for coaches on the platform, accepting less than 10% of coaches who apply. Coaches are required to come from elite educational and/or industry backgrounds, and receive vouches from several past clients before we approve them. While there are ranges of experience levels, all coaches are thoroughly vetted and high-quality.',
      },
      {
        question:
          'Can I switch coaches if the coach I’m matched with isn’t a fit?',
        answer:
          'Absolutely. We want to make sure you find the best coach for you. If at any moment you’d like to work on this package with another coach, we are happy to help you find a new one.',
      },
      {
        question: 'What’s included in Leland+?',
        answer:
          'Leland+ is a robust library of video content, example resources, templates, drills, and more. Over 100 of our best coaches have contributed to Leland+, and it continues to grow and improve every single week. If there’s something you’d like us to add to the Leland+ library, please reach out and we’ll work on your request!',
      },
      {
        question: 'What if I need more help after my package is finished?',
        answer:
          'If you need additional time, you can easily add coaching time to your balance later. Committing to more time upfront will discount the cost per hour.',
      },
      {
        question: 'Does coaching work in all time zones?',
        answer:
          'Yes! We have coaches all across the world in almost every timezone.',
      },
      {
        question: 'What if I need to cancel my session or I miss my session?',
        answer:
          'We understand that plans can change. We ask that you reschedule or cancel your session at least 24 hours ahead of time out of consideration for our coaches.',
      },
      {
        question: 'I have a question not answered here.',
        answer: (
          <p>
            Send us a message{' '}
            <a
              href={getHashUrlString(TYPEFORM_URLS.CUSTOMER_SUPPORT, {
                referrer: pathname,
                ...convertUrlSearchParamsToObject(searchParams),
              })}
              target="_blank"
              className="underline underline-offset-2"
              id="offering_package_page--contact_support"
            >
              here
            </a>
            .
          </p>
        ),
      },
    ],
    [pathname, searchParams],
  );

  return (
    <>
      {offeringPackage.imageLink && !isMd ? (
        <OfferingPackageImage
          containerClassName="w-full"
          name={offeringPackage.name}
          imageLink={offeringPackage.imageLink}
          sizes={{ dflt: '100vw' }}
        />
      ) : null}
      <div
        className={`${
          !styleAsCoachPackage ? 'col-span-12 block sm:hidden' : 'hidden '
        } sm:col-span-4 sm:w-auto`}
      >
        {!styleAsCoachPackage && !isSm ? (
          <OfferingPackageBanner
            coachToView={coachToUse ?? undefined}
            offeringPackage={offeringPackage}
            viewAllReviews={() => setIsAllReviewsModalOpen(true)}
            innerRef={bannerRef}
          />
        ) : null}
        <div className="my-6 flex flex-col rounded-xl">
          <OfferingPackagePricingInfo
            blocked={isUserBlocked}
            coachTier={tierToUse}
            coachView={coachView}
            isLoading={createOrderLoading}
            offeringPackage={offeringPackage}
            onMessageCoach={handleMessageCoach}
            onPurchaseOrSelectTier={handlePurchaseOrSelectTier}
            opportunityCoach={coachToUse}
            refetchQueries={refetchQueries}
            setCoachTier={setTierToUse}
          />
          <div className="mx-auto w-[90%]">
            <ScheduleACallSection />
          </div>
          {offeringPackage.coach.introVideo && isSm ? (
            <IntroVideo
              className="mt-3"
              compact
              video={offeringPackage.coach.introVideo}
              coach={offeringPackage.coach}
              categorySlug={offeringPackage.category?.slug}
            />
          ) : null}
        </div>
      </div>

      <div>
        {styleAsCoachPackage || isSm ? (
          <OfferingPackageBanner
            coachToView={coachToUse ?? undefined}
            offeringPackage={offeringPackage}
            viewAllReviews={() => setIsAllReviewsModalOpen(true)}
            innerRef={bannerRef}
          />
        ) : null}

        <div
          className={`mx-auto mb-8 max-w-page px-4 lg:px-6 ${
            !styleAsCoachPackage
              ? 'border-t border-leland-gray-stroke sm:border-t-0'
              : ''
          }`}
        >
          <div className="grid grid-cols-12 md:gap-14">
            <div className="col-span-12 md:col-span-8">
              {offeringPackage.description ? (
                <div className={`${!styleAsCoachPackage ? '' : 'mt-10'}`}>
                  {!styleAsCoachPackage ? null : (
                    <h2 className="text-2xl font-medium text-leland-gray-dark">
                      Package Description:
                    </h2>
                  )}
                  <p
                    ref={packageDescriptionRef}
                    className={`mt-4 whitespace-pre-line break-words text-lg font-normal leading-6 text-leland-gray-light ${
                      !expandedPackageDescription &&
                      packageDescriptionLines > 8 &&
                      'line-clamp-[8]'
                    }`}
                  >
                    {offeringPackage.description}
                  </p>
                  {packageDescriptionLines > 8 ? (
                    <button
                      className="link link--primary mt-0.5 text-base font-medium"
                      onClick={() =>
                        setExpandedPackageDescription((prevState) => !prevState)
                      }
                    >
                      {expandedPackageDescription ? 'Read less' : 'Read more'}
                    </button>
                  ) : null}
                </div>
              ) : null}

              {offeringPackage.category && styleAsCoachPackage && coach ? (
                <ReviewSlider
                  className="mt-10"
                  coach={offeringPackage.coach}
                  viewAllReviews={() => setIsAllReviewsModalOpen(true)}
                  categoryUrn={offeringPackage.category.id}
                />
              ) : null}

              {offeringPackage.deliverables ? (
                <OfferingPackageInfo heading="What you'll get from this package">
                  {offeringPackage.deliverables?.map((deliverable, index) => (
                    <div
                      key={index}
                      className="mt-4 flex items-start justify-start space-x-3 first:mt-0"
                    >
                      <SvgIcon
                        svgType={Svg.CHECK_CIRCLE_GREEN}
                        className="mt-0.5 size-5 shrink-0 fill-leland-primary text-white"
                      />
                      <p className="text-lg font-normal leading-6 text-leland-gray-light">
                        {deliverable}
                      </p>
                    </div>
                  ))}
                </OfferingPackageInfo>
              ) : null}
              {offeringPackage.packageTerm ? (
                <div className="mx-auto mb-12 max-w-page">
                  <hr className="my-8 md:my-12" />
                  <h2 className="text-2xl font-medium text-leland-gray-dark">
                    Additional details
                  </h2>
                  <div className="flex flex-col items-start justify-center gap-4">
                    {offeringPackage.deliveryFormat ? (
                      <InfoWithIcon
                        className="mt-4"
                        multiLineText={!!offeringPackage.coachingDescription}
                        Icon={
                          offeringPackage.deliveryFormat ===
                          CoachingDeliveryFormat.ASYNC
                            ? IconVideoAsync
                            : IconVideo
                        }
                        text={
                          <div className="text-lg font-normal leading-6 text-leland-gray-light">
                            <p>
                              Coaching delivered via{' '}
                              {
                                DELIVERY_FORMAT_TEXT[
                                  offeringPackage.deliveryFormat
                                ]
                              }
                              .
                            </p>
                            {offeringPackage.coachingDescription ? (
                              <>
                                <p className="whitespace-pre-line">
                                  {offeringPackage.coachingDescription}
                                </p>
                              </>
                            ) : null}
                          </div>
                        }
                      />
                    ) : null}
                    <InfoWithIcon
                      large
                      Icon={IconRefund}
                      text={REFUND_POLICY_HUMAN_READABLE[RefundPolicy.MODERATE]}
                      tooltip={REFUND_POLICY_DETAILS[RefundPolicy.MODERATE]}
                    />
                    {offeringPackage.packageTerm.additionalInformation ? (
                      <p className="mt-6 text-lg font-normal leading-6 text-leland-gray-light">
                        {offeringPackage.packageTerm.additionalInformation}
                      </p>
                    ) : null}
                    {offeringPackage.coachingFocusList?.length ? (
                      <div className="mt-4 flex flex-col gap-2 text-lg text-leland-gray-light">
                        <p>Services included:</p>
                        <div className="flex flex-wrap gap-2">
                          {offeringPackage.coachingFocusList.map(
                            (coachingFocus) => (
                              <Tag
                                key={coachingFocus.id}
                                text={coachingFocus.name}
                                fontWeight={FontWeight.NORMAL}
                                tagColor={TagColor.TRANSPARENT}
                                size={TagSize.LARGE}
                              />
                            ),
                          )}
                        </div>
                      </div>
                    ) : null}
                    <LelandExperienceGuarantee className="mt-4 md:hidden" />
                  </div>
                </div>
              ) : null}
            </div>
            <div className="hidden md:col-span-4 md:block md:w-auto">
              <div
                className="mt-10 flex flex-col rounded-xl"
                style={{
                  marginTop: bannerSize?.height
                    ? `-${bannerSize.height - 32}px`
                    : '-9.25rem',
                }}
              >
                <OfferingPackagePricingInfo
                  blocked={isUserBlocked}
                  coachTier={tierToUse}
                  coachView={coachView}
                  isLoading={createOrderLoading}
                  offeringPackage={offeringPackage}
                  onMessageCoach={handleMessageCoach}
                  onPurchaseOrSelectTier={handlePurchaseOrSelectTier}
                  opportunityCoach={coachToUse}
                  refetchQueries={refetchQueries}
                  setCoachTier={setTierToUse}
                />
                <ScheduleACallSection />
                {coach?.introVideo && isSm ? (
                  <IntroVideo
                    className="mt-3"
                    compact
                    video={coach?.introVideo}
                    coach={coach}
                    categorySlug={offeringPackage.category?.slug}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {!styleAsCoachPackage && reorganizedCoaches.length > 2 ? (
          <div className="my-12 border-y border-leland-gray-stroke bg-leland-beige py-24">
            <div className="mx-auto max-w-page px-4">
              <h2 className="text-3xl font-medium text-leland-gray-dark">
                Popular coaches for this package
              </h2>
              <p className="mb-6 text-leland-gray-light">
                Rest assured that Leland has a perfect match for you in our
                network of thousands of expert coaches.
              </p>
              <Carousel
                swiperProps={{
                  slidesPerGroupAuto: false,
                  spaceBetween: 20,
                  slidesPerView: isLg ? 4 : isMd ? 3 : 1.15,
                }}
                buttonSize={ButtonSize.MEDIUM}
                disableArrowGradient
                items={reorganizedCoaches.map((coach) => (
                  <div key={coach.id} className="size-full bg-white">
                    <VerticalCoachCard
                      coach={coach}
                      categoryUrn={offeringPackage.category?.id ?? ''}
                      hidePrice
                    />
                  </div>
                ))}
              />
              <CoachMatchingSupportBanner />
            </div>
          </div>
        ) : null}

        {offeringPackage.category && styleAsCoachPackage && coach ? (
          <div className={SECTION_SPACING_CLASSNAME}>
            <hr className="my-8 md:my-12" />
            <CoachOfferings
              livePackages={livePackages}
              coach={coach}
              categoryUrn={offeringPackage.category.id}
              categorySlug={offeringPackage.category.slug}
              services={services}
              onGetStarted={handleBuyCoaching}
              headline={`More packages from ${coach.user.firstName}`}
              blocked={isUserBlocked}
            />
          </div>
        ) : null}

        {offeringPackage.category && styleAsCoachPackage && coach ? (
          <div className={SECTION_SPACING_CLASSNAME}>
            <hr className="my-8 md:my-12" />
            <OfferingPackageCoachAbout
              coach={coach}
              category={offeringPackage.category}
              viewAllReviews={() => setIsAllReviewsModalOpen(true)}
            />
            {relatedPackages.length ? (
              <>
                <hr className="my-8 md:my-12" />
                <h2 className="mb-2 text-left text-2xl font-medium text-leland-gray-dark md:mb-8">
                  Browse related packages
                </h2>
                <div className="grid md:grid-cols-2 md:gap-4 xl:grid-cols-4">
                  {relatedPackages.map((p) => (
                    <OfferingPackageCard
                      key={p.id}
                      offeringPackage={p}
                      opportunityCoach={coachToUse}
                    />
                  ))}
                </div>
              </>
            ) : null}
          </div>
        ) : null}

        {offeringPackage.category && isLelandPackage && !styleAsCoachPackage ? (
          <div className={SECTION_SPACING_CLASSNAME}>
            <PackageReviews
              category={offeringPackage.category}
              onViewAllReviews={() => setIsAllPackageReviewsModalOpen(true)}
            />
          </div>
        ) : null}

        {!styleAsCoachPackage ? (
          <div className={SECTION_SPACING_CLASSNAME}>
            <hr className="my-8 md:my-12" />
            <Faq title="Frequently asked questions" items={FAQ_QUESTIONS} />
          </div>
        ) : null}
      </div>

      {isBookHourlyCoachingModalOpen || !styleAsCoachPackage ? null : (
        <OfferingPackageBottomCTA
          coach={coach}
          offeringPackage={offeringPackage}
          onPurchaseOrSelectTier={handlePurchaseOrSelectTier}
          isLoading={createOrderLoading}
          blocked={isUserBlocked}
        />
      )}

      <LoginModal />

      <AllCoachReviewsModal
        coach={offeringPackage.coach}
        hasReviewed={hasReviewed}
        setReviewModalOpen={() => {
          setIsAllReviewsModalOpen(false);
          setIsReviewModalOpen(true);
        }}
        categoryUrn={offeringPackage.category?.id}
      />

      {offeringPackage.category?.id ? (
        <AllPackageReviewsModal categoryUrn={offeringPackage.category.id} />
      ) : null}

      {offeringPackage.category?.id ? (
        <LeaveAReviewModal
          coach={offeringPackage.coach}
          defaultValues={{
            ...DEFAULT_REVIEW_FORM_VALUES,
            categoryReviews: [{ categoryUrn: offeringPackage.category.id }],
          }}
          onSuccess={onReviewSuccess}
        />
      ) : null}

      {currentUser?.applicant?.id &&
      offeringPackage.coach.categoryInformationList &&
      offeringPackage.category?.id ? (
        <OutcomeModal
          categoryInfo={offeringPackage.coach.categoryInformationList}
          coachName={offeringPackage.coach.user.firstName}
          defaultValues={{
            applicantUrn: currentUser.applicant.id,
            categoryUrn: offeringPackage.category.id,
          }}
        />
      ) : null}

      {offeringPackage.category && coach ? (
        <MessageModal
          coach={coach}
          categoryUrn={offeringPackage.category.id}
          defaultMessage={`Hi ${coach.user.firstName}! I'm interested in purchasing your package: ${offeringPackage.name}. Are you free to chat sometime in the next few days about how we might work together?`}
          onSuccess={refetchPersonalizedCoach}
        />
      ) : null}

      {offeringPackage ? (
        <SelectATierModal
          offeringPackage={offeringPackage}
          onChoose={(tier: OfferingPackagePrice) => handleChoosePackage(tier)}
          chosenTier={chosenTier}
          isLoading={createOrderLoading}
        />
      ) : null}

      {offeringPackage ? (
        <SelectAPackageSizeModal
          coachView={coachView}
          isLoading={createOrderLoading}
          offeringPackage={offeringPackage}
          onChoose={(tier: OfferingPackagePrice) => handleChoosePackage(tier)}
          skuTier={tierToUse}
        />
      ) : null}

      {!hourlyOrderLoading && coachToUse ? (
        <BookHourlyCoachingModal
          categoryUrn={offeringPackage.category?.id}
          coach={coachToUse}
          services={services}
          defaultValues={{
            coachUrn: coachToUse.id,
            hourlyOrder: {
              timeInMinute:
                hourlyOrderData?.order?.hourlyOrder?.timeInMinute ??
                mostRecentHourlyOrder?.timeInMinute ??
                DEFAULT_HOURLY_ORDER_MINUTES,
            },
            numberOfCharges: 1,
            categoryUrn: offeringPackage.category?.id,
          }}
          orderUrn={hourlyOrderUrn}
        />
      ) : null}
    </>
  );
};

export default OfferingPackagePage;
